/**
 * The source language.
 *
 * Its keys define the shape every other locale must satisfy — `fa.ts` is typed
 * against this object, so adding a string here without translating it is a
 * compile error rather than a silent English word in the middle of Persian.
 */
export const en = {
  // ---- navigation and chrome ----
  'nav.main': 'Main',
  'nav.dashboard': 'Dashboard',
  'nav.gaps': 'Gaps',
  'nav.rsi': 'RSI',
  'nav.timeWindows': 'Time Windows',
  'nav.volume': 'Volume Area',
  'volume.title': 'Volume Area',
  'volume.intro':
    'Where trade actually happened, by price. Heavy rows are prices the market accepted and tends to hold around; thin rows are prices it refused and raced through, which is where a return is most often rejected.',
  'volume.chart': 'Volume profile',
  'volume.structure': 'Profile',
  'volume.reversalLevels': 'Reversal potential',
  'volume.rows': '{n} rows',
  'volume.poc': 'Point of control',
  'volume.vah': 'Value area high',
  'volume.val': 'Value area low',
  'volume.lvn': 'Low volume node',
  'volume.hvn': 'High volume node',
  'volume.price': 'Last price',
  'volume.noData': 'No {timeframe} candles to profile yet.',
  'volume.noLevels': 'Nothing worth marking on this profile.',
  'volume.caption':
    '{bars} {timeframe} bars in {rows} rows of {size} · value area holds {pct}% of the volume · tick volume, not contracts traded',
  'volume.insideNote': 'Price is inside the value area — the range it has been accepting.',
  'volume.outsideNote': 'Price is outside the value area, so the profile describes where it came from rather than where it is trading.',
  'volume.scoreNote':
    'Ranked by how sharply the profile marks the price and how near it is. A low volume node scores highest: price refused that level once and tends to refuse it again.',
  'nav.knots': 'Knots',
  'nav.fibonacci': 'Fibonacci',
  'shell.settings': 'Settings',
  'shell.signOut': 'Sign out',
  'shell.lightTheme': 'Light theme',
  'shell.systemTheme': 'Follow device',
  'shell.switchToSystem': 'Follow the device theme',
  'shell.darkTheme': 'Dark theme',
  'shell.switchToDark': 'Switch to dark theme',
  'shell.switchToLight': 'Switch to light theme',
  'shell.tradingPair': 'Trading pair',
  'shell.language': 'Language',

  'app.loading': 'Loading',
  'app.unreachable': 'Cannot reach the server',

  // ---- shared vocabulary ----
  'common.refresh': 'Refresh',
  'common.loading': 'Loading…',
  'common.none': 'None',
  'common.above': 'above',
  'common.below': 'below',
  'common.bullish': 'Bullish',
  'common.bearish': 'Bearish',
  'common.neutral': 'Neutral',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.tolerance': 'Tolerance',
  'common.fullScreen': 'Full screen',
  'common.brokerTime': 'broker time',
  'common.today': 'Today',
  'common.exitFullScreen': 'Exit full screen (Esc)',
  'common.chartFullScreen': '{name} full screen',
  'common.bar': 'bar',
  'common.bars': 'bars',

  'importance.1': 'Medium',
  'importance.2': 'High',
  'importance.3': 'Very high',

  // ---- dashboard ----
  'dashboard.title': 'Dashboard',
  'dashboard.subtitle': '{symbol} analysis overview.',

  // ---- gaps ----
  'gaps.title': 'Gaps',
  'gaps.chart': 'Gaps chart',
  'gaps.minSize': 'Min size',
  'gaps.modeStrict': 'Strict',
  'gaps.modeUntraded': 'Untraded',
  'gaps.abovePrice': 'Above price',
  'gaps.belowPrice': 'Below price',
  'gaps.unfilledAbove': 'Unfilled gaps above price',
  'gaps.unfilledBelow': 'Unfilled gaps below price',
  'gaps.derivedNote': 'Derived from long H1 history — shown here but excluded from the dashboard bias',
  'gaps.noInstruments': 'No instruments yet',
  'gaps.levels': '{timeframe} levels',
  'gaps.widgetSummary': '{count} unfilled on {frames}',

  // ---- rsi ----
  'rsi.title': 'RSI',
  'rsi.chart': 'RSI chart',
  'rsi.period': 'Period',
  'rsi.divergences': 'Divergences',
  'rsi.dashedHidden': 'dashed = hidden',
  'rsi.widgetSummary': '{count} divergences · {bullish} bullish / {bearish} bearish',

  // ---- time windows and points ----
  'tw.title': 'Time Windows',
  'tw.subtitle': '{symbol} · recurring sessions shaded on the chart',
  'tw.chart': 'Time windows chart',
  'tw.windows': 'Windows',
  'tw.noWindows': 'No windows configured for this pair.',
  'tw.occurrences': 'Occurrences',
  'tw.closedUp': 'Closed up',
  'tw.avgRange': 'Avg range',
  'tw.avgMove': 'Avg move',
  'tw.brokerClock': 'Broker clock',
  'tw.zoneTime': '{zone} time',
  'tw.tooCoarse': 'No occurrences on this timeframe — its bars are longer than the window.',
  'tw.error': 'Could not load time windows.',

  'points.title': 'Time Points',
  'points.nextUp': 'Next up',
  'points.none': 'No time points configured.',
  'points.untilFires': 'until it fires',
  'points.alertKind': 'Time point',
  'points.now': 'now',
  'points.then': 'Then',
  'points.hint': 'Counting down to the next occurrence. Click to hide a point on the chart.',
  'points.dismiss': 'Dismiss {name} alert',
  'points.broker': 'broker',

  // ---- knots ----
  'fib.title': 'Fibonacci',
  'fib.subtitle': '{symbol} · retracements on swings',
  'fib.chart': 'Fibonacci chart',
  'fib.error': 'Could not load candles.',
  'fib.noCandles': 'No candles for {timeframe} yet.',
  'fib.barsLoaded': '{count} bars',
  'fib.widgetNote': 'The detected swing per timeframe and how far price has retraced it.',
  'fib.swingUp': 'Up swing',
  'fib.swingDown': 'Down swing',
  'fib.retraced': 'Retraced',
  'fib.noSwing': 'No swing drawn on this timeframe yet.',
  'fib.candles': 'Candles',
  'fib.ladder': 'Levels, all timeframes',
  'fib.ladderNote': 'Every level of every live swing on one price axis, nearest the current price in the middle. 0.559, 0.702 and 0.118 are the ones under examination.',
  'fib.swings': 'Swings',
  'fib.price': 'price',
  'fib.running': 'end still running',
  'fib.draw': 'Draw fib',
  'fib.drawing': 'Drawing: click the start, then the end',
  'fib.drawStart': 'Click the start of the swing',
  'fib.drawEnd': 'Now click the end of the swing',
  'fib.cancel': 'Cancel',
  'fib.yourDraws': 'Your draws',
  'fib.yourDrawsNote': 'Fibonaccis you drew on the chart, kept as teaching examples. Add a note to each so the reasoning is saved with it.',
  'fib.noDraws': 'Nothing drawn yet. Press Draw fib, then click the start and the end of a swing.',
  'fib.notePlaceholder': 'Why this swing? (saved when you leave the field)',
  'fib.showMine': 'Show my draws',
  'fib.showYours': 'Show your draws',
  'fib.mySwings': 'My swings',
  'fib.auto': 'auto',
  'fib.showPivots': 'Show pivots',
  'fib.reversal': 'reversal ≥ {pct}%',
  'fib.score': '{hits} OHLC hits',
  'fib.refinedOn': 'ends on {tfs}',
  'fib.autoNone': 'No swing found yet.',
  'fib.tabCharts': 'Charts',
  'fib.tabResearch': 'Research',
  'fib.researchSubtitle': 'Studies on the chart history, written up for you. Leave a comment under any note.',
  'fib.noResearch': 'No research notes yet.',
  'fib.yourComment': 'Your comment',
  'fib.commentPlaceholder': 'Agree, disagree, or what to look at next (saved when you leave the field)',
  'fib.approve': 'Approve',
  'fib.deny': 'Deny',
  'fib.verdictNotePlaceholder': 'Why? (optional, saved when you leave the field)',
  'fib.prevDraw': 'Previous drawing',
  'fib.nextDraw': 'Next drawing',
  'fib.noNote': 'no note yet',
  'fib.removedCount': '{n} removed swing(s) not shown.',
  'fib.removedStart': 'Swing removed — price passed its start ({price}) at {time}',
  'fib.removedEnd': 'Swing removed — price passed its end ({price}) at {time}',
  'knots.title': 'Knots',
  'knots.subtitle': '{symbol} · body midpoints, and the closes that take them',
  'knots.chart': 'Knots chart',
  'knots.showBand': 'Show accepting band',
  'knots.legend': 'taken knots only, one colour each ·',
  'knots.latest': 'latest',
  'knots.thisWindow': 'This window',
  'knots.formed': 'Knots formed',
  'knots.taken': 'Taken',
  'knots.stillLive': 'Still live',
  'knots.medianWait': 'Median wait',
  'knots.takenFromAbove': 'Taken from above',
  'knots.fromBelow': 'From below',
  'knots.liveAbove': 'Live knots above',
  'knots.liveBelow': 'Live knots below',
  'knots.noneInRange': 'None in range.',
  'knots.rateNote':
    'Rate is over every knot formed, including ones too recent to have been reached — so it reads slightly low near the right edge.',
  'knots.error': 'Could not load knots.',
  'knots.barsUnit': '{count} bars',

  'knots.alertTaken': 'Knot taken · {timeframe}',
  'knots.biasBullish': 'red close on the level — bullish',
  'knots.biasBearish': 'green close on the level — bearish',
  'knots.fellOnto': 'price fell onto the level',
  'knots.roseInto': 'price rose into the level',
  'knots.fellOntoTitle': 'Price fell onto the level',
  'knots.roseIntoTitle': 'Price rose into the level',
  'knots.held': 'held {count} {unit}',
  'knots.dismissAlert': 'Dismiss knot alert',
  'knots.alertKind': 'Knot taken',
  'knots.levelCaption': 'Level',
  'knots.pending': 'pending',
  'knots.alertPending': 'Knot pending · {timeframe}',
  'knots.pendingNeedsBull': 'Counts if the next candle closes bullish',
  'knots.pendingNeedsBear': 'Counts if the next candle closes bearish',
  'knots.dismissPending': 'Dismiss pending knot alert',
  'knots.justTaken': 'just taken',
  'knots.noneTaken': 'No knot has been taken on any timeframe in range.',
  'knots.barsLater': '{count} {unit} later',
  // ---- settings ----
  'settings.build': 'Build {version}',
  'settings.title': 'Settings',
  'settings.subtitle': 'Trading pairs and the time markers drawn on your charts.',
  'settings.windowsDescription': 'Shaded bands spanning a period of the day.',
  'settings.pointsDescription': 'Dashed vertical lines at a single moment of the day.',
  'settings.tradingPairs': 'Trading pairs',
  'settings.timeWindows': 'Time windows',
  'settings.timePoints': 'Time points',
  'settings.allPairs': 'All pairs',
  'settings.noPairs': 'No pairs ingested yet.',
  'settings.name': 'Name',
  'settings.description': 'Description',
  'settings.descriptionPlaceholder': 'What this marks',
  'settings.start': 'Start',
  'settings.end': 'End',
  'settings.time': 'Time',
  'settings.colour': 'Colour',
  'settings.customColour': 'Custom colour',
  'settings.days': 'Days',
  'settings.importance': 'Importance',
  'settings.timezone': 'Timezone',
  'settings.addWindow': 'Add window',
  'settings.addPoint': 'Add point',
  'settings.saveWindow': 'Save window',
  'settings.savePoint': 'Save point',
  'settings.sounds': 'Alert sounds',
  'settings.soundsDescription': 'What each alert sounds like. Pick one to hear it.',
  'settings.soundsNote':
    'Saved on this device, not to your account — so a phone can stay silent while the desk does not. Browsers hold audio until the page has been clicked once.',
  'settings.soundKnot': 'Knot taken',
  'settings.soundKnotPending': 'Knot pending confirmation',
  'settings.soundPoint': 'Time point approaching',
  'settings.soundVolume': 'Volume',
  'settings.sound_none': 'Silent',
  'settings.sound_chime': 'Chime',
  'settings.sound_bell': 'Bell',
  'settings.sound_blip': 'Blip',
  'settings.sound_knock': 'Knock',
  'settings.sound_rise': 'Rise',
  'settings.kindWick': 'Wick',
  'settings.kindOpen': 'Open',
  'settings.kindClose': 'Close',
  'settings.dirAny': 'Either',
  'settings.dirUp': 'Up',
  'settings.dirDown': 'Down',

  // ---- login ----
  'login.welcome': 'Welcome back',
  'login.username': 'Username',
  'login.usernamePlaceholder': 'ertano',
  'login.createPasskey': 'Create passkey',
  'login.signIn': 'Sign in with passkey',
  'login.enter': 'Enter',
  'login.noPasskeys': 'Passkeys not available',

  // ---- price alerts ----
  'nav.alerts': 'Alerts',
  'alerts.title': 'Price Alerts',
  'alerts.subtitle': '{symbol} · drag a line, hear it when price gets there',
  'alerts.chart': 'Price alerts chart',
  'alerts.live': 'live',
  'alerts.stale': 'stale',
  'alerts.staleNote': 'The price feed has not updated for a while, so no alert can fire until it does. Check that MetaTrader is connected.',
  'alerts.error': 'Could not load price alerts.',
  'alerts.noCandles': 'No M5 candles yet.',
  'alerts.newLine': 'New line',
  'alerts.clickChart': 'Click the chart to place it',
  'alerts.hint': 'Double-click to add · drag a line to move it',
  'alerts.delivery': 'Sound & notification',
  'alerts.testSound': 'Test sound',
  'alerts.notifyEnable': 'Enable notifications',
  'alerts.notifyBlocked': 'Notifications blocked',
  'alerts.notifyOn': 'Notifications on',
  'alerts.notifyUnsupported': 'This browser cannot show notifications.',
  'alerts.notifyTestBody': 'Notifications are working.',
  'alerts.deliveryNote': 'Alerts are checked in the browser, so Pipyar has to be open in a tab (it can be in the background). The sound is set in Settings → Alert sounds.',
  'alerts.lines': 'Lines',
  'alerts.none': 'No alerts on this pair. Double-click the chart to add one.',
  'alerts.price': 'Alert price',
  'alerts.notePlaceholder': 'Add a note…',
  'alerts.on': 'On',
  'alerts.rearm': 'Arm again',
  'alerts.delete': 'Delete alert',
  'alerts.firesAbove': 'Fires when price rises to this line',
  'alerts.firesBelow': 'Fires when price falls to this line',
  'alerts.firedAt': 'Fired at {price} · {time}',
  'alerts.crossedAbove': 'reached',
  'alerts.crossedBelow': 'fell to',
  'alerts.now': 'now',
  'alerts.priceAlert': 'Price alert',
  'alerts.dismiss': 'Dismiss price alert',
  'alerts.stop': 'Stop alarm',
  'alerts.backBelow': 'Price is back below the line · silent until it crosses again',
  'alerts.backAbove': 'Price is back above the line · silent until it crosses again',
  'alerts.snooze': 'Snooze',
  'alerts.snoozeFor': 'Snooze for {n} minutes',
  'alerts.snoozed': 'Snoozed · rings again in',
  'alerts.unsnooze': 'Ring now',
  'settings.soundPrice': 'Price alert line',
  'settings.priceRepeat': 'Repeat',
  'settings.priceRepeatTimes': '{n}×',
  'settings.priceRepeatUntilDismissed': 'Until dismissed',

  // ---- feed status ----
  'feed.label': 'MT5 data',
  'feed.tooltip': 'When MetaTrader last wrote a price. Red means the feed has stopped.',
  'feed.unknown': 'no data',
  'feed.secondsAgo': '{n}s ago',
  'feed.minutesAgo': '{n}m ago',
  'feed.hoursAgo': '{n}h ago',
  'feed.daysAgo': '{n}d ago',
  'feed.bannerTitle': 'MT5 data is not live',
  'feed.bannerStale': 'No new prices from MetaTrader. Charts, signals and price alerts are frozen.',
  'feed.bannerDisconnected': 'MetaTrader has lost its broker connection. Charts, signals and price alerts are frozen.',
  'feed.bannerUnreachable': 'Cannot get prices from the server. Charts, signals and price alerts are frozen.',
  'feed.bannerLast': 'last update {time} ({age})',
} as const;
